import { microdataConfig } from './microdata-config';
import type { Thing, WithContext } from 'schema-dts';

const SCHEMA_DAY_OF_WEEK = [
  'https://schema.org/Monday',
  'https://schema.org/Tuesday',
  'https://schema.org/Wednesday',
  'https://schema.org/Thursday',
  'https://schema.org/Friday',
  'https://schema.org/Saturday',
  'https://schema.org/Sunday',
] as const;

function buildSameAs() {
  const { site, local } = microdataConfig;
  const sameAs = [local.mapUrl];

  if (site.social.instagram) sameAs.push(site.social.instagram);
  if (site.social.facebook) sameAs.push(site.social.facebook);

  return sameAs;
}

function buildPostalAddress() {
  const { local } = microdataConfig;

  return {
    '@type': 'PostalAddress' as const,
    streetAddress: local.address.street,
    addressLocality: local.address.city,
    addressRegion: local.address.region,
    postalCode: local.address.postal,
    addressCountry: local.address.country,
  };
}

function buildLogo() {
  const { site } = microdataConfig;

  return {
    '@type': 'ImageObject' as const,
    url: site.logo,
    width: 512,
    height: 512,
  };
}

function buildOrganizationEntity() {
  const { site, homepage } = microdataConfig;

  return {
    '@type': 'Organization' as const,
    '@id': `${site.url}#org`,
    name: site.name,
    url: site.url,
    image: site.defaultImage,
    description: homepage.description,
    email: site.email,
    telephone: site.phone,
    sameAs: buildSameAs(),
    logo: buildLogo(),
    address: buildPostalAddress(),
  };
}

/**
 * Generate JSON-LD structured data for the homepage
 * Following schema.org standards for Organization, WebSite, WebPage, HVACBusiness, and OfferCatalog
 */
export function generateHomepageJsonLd(): WithContext<Thing>[] {
  const { site, homepage, local, services } = microdataConfig;
  const organization = buildOrganizationEntity();

  return [
    {
      '@context': 'https://schema.org',
      ...organization,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${site.url}#website`,
      url: site.url,
      name: site.name,
      publisher: { '@id': `${site.url}#org` },
      inLanguage: 'en-US',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${homepage.url}#webpage`,
      url: homepage.url,
      name: homepage.title,
      isPartOf: { '@id': `${site.url}#website` },
      about: { '@id': `${site.url}#org` },
      description: homepage.description,
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: site.defaultImage,
        width: 1200,
        height: 630,
      },
      datePublished: homepage.datePublished,
      dateModified: homepage.dateModified,
      inLanguage: 'en-US',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HVACBusiness',
      '@id': `${site.url}#local`,
      name: site.name,
      url: site.url,
      image: site.defaultImage,
      logo: buildLogo(),
      priceRange: local.priceRange,
      telephone: site.phone,
      email: site.email,
      sameAs: buildSameAs(),
      hasMap: local.mapUrl,
      additionalType: [
        'https://schema.org/Electrician',
        'https://schema.org/Plumber',
        'https://schema.org/HomeAndConstructionBusiness',
      ],
      address: buildPostalAddress(),
      geo: {
        '@type': 'GeoCoordinates',
        latitude: local.geo.lat,
        longitude: local.geo.lng,
      },
      areaServed: local.areaServed.map((area) => ({
        '@type': 'Place',
        name: area,
      })),
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: [...SCHEMA_DAY_OF_WEEK],
          opens: local.hours.weekdays.opens,
          closes: local.hours.weekdays.closes,
        },
      ],
      parentOrganization: organization,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'OfferCatalog',
      '@id': `${site.url}#catalog`,
      name: `${site.name} Services`,
      url: services.url,
      itemListElement: [
        {
          '@type': 'OfferCatalog',
          name: 'HVAC',
          itemListElement: [
            {
              '@type': 'Service',
              '@id': `${services.hvac.install}#service`,
              name: 'HVAC Installation',
              url: services.hvac.install,
            },
            {
              '@type': 'Service',
              '@id': `${services.hvac.repair}#service`,
              name: 'HVAC Repair',
              url: services.hvac.repair,
            },
            {
              '@type': 'Service',
              '@id': `${services.hvac.maintenance}#service`,
              name: 'HVAC Maintenance',
              url: services.hvac.maintenance,
            },
            {
              '@type': 'Service',
              '@id': `${services.hvac.acInstall}#service`,
              name: 'AC Installation',
              url: services.hvac.acInstall,
            },
            {
              '@type': 'Service',
              '@id': `${services.hvac.acRepair}#service`,
              name: 'AC Repair',
              url: services.hvac.acRepair,
            },
            {
              '@type': 'Service',
              '@id': `${services.hvac.ductless}#service`,
              name: 'Ductless Systems',
              url: services.hvac.ductless,
            },
          ],
        },
        {
          '@type': 'OfferCatalog',
          name: 'Electrical',
          itemListElement: [
            {
              '@type': 'Service',
              '@id': `${services.electrical.install}#service`,
              name: 'Electrical Installation',
              url: services.electrical.install,
            },
            {
              '@type': 'Service',
              '@id': `${services.electrical.panel}#service`,
              name: 'Panel Upgrades',
              url: services.electrical.panel,
            },
            {
              '@type': 'Service',
              '@id': `${services.electrical.repair}#service`,
              name: 'Electrical Repair',
              url: services.electrical.repair,
            },
          ],
        },
        {
          '@type': 'OfferCatalog',
          name: 'Plumbing',
          itemListElement: [
            {
              '@type': 'Service',
              '@id': `${services.plumbing.install}#service`,
              name: 'Plumbing Installation',
              url: services.plumbing.install,
            },
            {
              '@type': 'Service',
              '@id': `${services.plumbing.repair}#service`,
              name: 'Plumbing Repair',
              url: services.plumbing.repair,
            },
            {
              '@type': 'Service',
              '@id': `${services.plumbing.waterHeaters}#service`,
              name: 'Water Heaters',
              url: services.plumbing.waterHeaters,
            },
          ],
        },
        {
          '@type': 'OfferCatalog',
          name: 'Remodeling',
          itemListElement: [
            {
              '@type': 'Service',
              '@id': `${services.remodeling.kitchen}#service`,
              name: 'Kitchen Remodeling',
              url: services.remodeling.kitchen,
            },
            {
              '@type': 'Service',
              '@id': `${services.remodeling.bathroom}#service`,
              name: 'Bathroom Remodeling',
              url: services.remodeling.bathroom,
            },
          ],
        },
        {
          '@type': 'OfferCatalog',
          name: 'Construction',
          itemListElement: [
            {
              '@type': 'Service',
              '@id': `${services.construction.residential}#service`,
              name: 'Residential Construction',
              url: services.construction.residential,
            },
            {
              '@type': 'Service',
              '@id': `${services.construction.commercial}#service`,
              name: 'Commercial Construction',
              url: services.construction.commercial,
            },
          ],
        },
      ],
    },
  ] as WithContext<Thing>[];
}

/**
 * Generate JSON-LD structured data for the About Us page
 * Following schema.org standards for Organization, WebSite, AboutPage, and BreadcrumbList
 */
export function generateAboutPageJsonLd(): WithContext<Thing>[] {
  const { site, about, organization } = microdataConfig;

  // Build sameAs array for social links
  const sameAs: string[] = [];
  if (site.social.instagram) sameAs.push(site.social.instagram);
  if (site.social.facebook) sameAs.push(site.social.facebook);

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${site.url}#org`,
      name: site.name,
      url: site.url,
      logo: {
        '@type': 'ImageObject',
        url: site.logo,
        width: 512,
        height: 512,
      },
      email: site.email,
      telephone: site.phone,
      ...(organization.slogan && { slogan: organization.slogan }),
      ...(organization.foundingDate && {
        foundingDate: organization.foundingDate,
      }),
      ...(sameAs.length > 0 && { sameAs }),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${site.url}#website`,
      url: site.url,
      name: site.name,
      publisher: { '@id': `${site.url}#org` },
      inLanguage: 'en-US',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${about.url}#webpage`,
      url: about.url,
      name: about.title,
      isPartOf: { '@id': `${site.url}#website` },
      about: { '@id': `${site.url}#org` },
      description: about.description,
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: about.image,
        width: 1200,
        height: 630,
      },
      breadcrumb: { '@id': `${about.url}#breadcrumb` },
      ...(about.datePublished && { datePublished: about.datePublished }),
      ...(about.dateModified && { dateModified: about.dateModified }),
      inLanguage: 'en-US',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${about.url}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: site.url,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: about.breadcrumbParentName,
          item: about.breadcrumbParentUrl,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'About Us',
          item: about.url,
        },
      ],
    },
  ] as WithContext<Thing>[];
}

/**
 * Breadcrumb item type
 */
export type BreadcrumbItem = {
  name: string;
  url: string;
};

/**
 * FAQ item type
 */
export type FAQItem = {
  question: string;
  answer: string;
};

/**
 * Generate standalone breadcrumb JSON-LD for any page
 * @param breadcrumbs - Array of breadcrumb items (Home is automatically prepended)
 * @param currentPageName - Name of the current page
 * @param currentPageUrl - URL of the current page
 */
export function generateBreadcrumbJsonLd(
  breadcrumbs: BreadcrumbItem[],
  currentPageName: string,
  currentPageUrl: string
): WithContext<Thing> {
  const { site } = microdataConfig;

  // Build breadcrumb list starting with Home
  const itemListElement = [
    {
      '@type': 'ListItem' as const,
      position: 1,
      name: 'Home',
      item: site.url,
    },
    ...breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem' as const,
      position: index + 2,
      name: crumb.name,
      item: crumb.url,
    })),
    {
      '@type': 'ListItem' as const,
      position: breadcrumbs.length + 2,
      name: currentPageName,
      item: currentPageUrl,
    },
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  } as WithContext<Thing>;
}

/**
 * Generate simple two-level breadcrumb (Home → Current Page)
 */
export function generateSimpleBreadcrumbJsonLd(
  pageName: string,
  pageUrl: string
): WithContext<Thing> {
  const { site } = microdataConfig;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: site.url,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: pageName,
        item: pageUrl,
      },
    ],
  } as WithContext<Thing>;
}

/**
 * Generate FAQ JSON-LD for FAQ pages
 * @param faqs - Array of FAQ items with question and answer
 */
export function generateFAQJsonLd(
  faqs: readonly FAQItem[] | FAQItem[]
): WithContext<Thing> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  } as WithContext<Thing>;
}

/**
 * Generate Contact Page JSON-LD
 * Includes ContactPage and Organization with contactPoint
 */
export function generateContactPageJsonLd(): WithContext<Thing>[] {
  const { site, contact } = microdataConfig;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${contact.url}#webpage`,
      url: contact.url,
      name: contact.title,
      description: contact.description,
      isPartOf: {
        '@id': `${site.url}#website`,
      },
      about: {
        '@id': `${site.url}#org`,
      },
      inLanguage: 'en-US',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${site.url}#org`,
      name: site.name,
      url: site.url,
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          telephone: site.phone,
          areaServed: contact.areaServed,
          availableLanguage: contact.languages,
        },
      ],
    },
  ] as WithContext<Thing>[];
}

/**
 * Article item type for blog posts
 */
export type ArticleData = {
  url: string;
  title: string;
  description: string;
  image: string;
  author: string;
  datePublished: string;
  dateModified: string;
};

/**
 * Generate Article JSON-LD for blog posts
 * @param article - Article data with URL, title, description, image, author, and dates
 */
export function generateArticleJsonLd(
  article: ArticleData
): WithContext<Thing> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    mainEntityOfPage: article.url,
  } as WithContext<Thing>;
}

/**
 * Helper function to safely stringify JSON-LD data
 * Prevents XSS by escaping < > & characters
 */
export function safeJsonLdStringify(data: unknown): string {
  return JSON.stringify(data, null, 2)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}
